import React from 'react';
import { SectionContainer } from '../components/SectionContainer';
import SectionTitle from '../components/SectionTitle';
import { SectionBody } from '../components/SectionBody';
import { SectionLine } from '../components/SectionLine';
import { FormikPremiumInput } from 'modules/emergencies/components/ui/inputs/customInput';
import { FieldErrorMessageContainer, FieldErrorMessageText } from '../../ui/fieldErrorsMessage';
import usePostalCodeFilter from '../usePostalCodeFilter';

const t = {
  error: 'No postal code found, try again later',
  no_zipcode: 'No postal code found',
  zipcode_label: 'Postal code',
  search_label: 'Search postal code',
};

const countryOptions = [
  {
    value: 'ES',
    label: 'Spain',
  },
  {
    value: 'AD',
    label: 'Andorra',
  },
  {
    value: 'AT',
    label: 'Austria',
  },
  {
    value: 'BE',
    label: 'Belgium',
  },
  {
    value: 'BG',
    label: 'Bulgaria',
  },
  {
    value: 'CH',
    label: 'Switzerland',
  },
  {
    value: 'CZ',
    label: 'Czechia',
  },
  {
    value: 'DE',
    label: 'Germany',
  },
  {
    value: 'DK',
    label: 'Denmark',
  },
  {
    value: 'DZ',
    label: 'Algeria',
  },
  {
    value: 'EE',
    label: 'Estonia',
  },
  {
    value: 'FI',
    label: 'Finland',
  },
  {
    value: 'FR',
    label: 'France',
  },
  {
    value: 'GB',
    label: 'UK',
  },
  {
    value: 'GR',
    label: 'Greece',
  },
  {
    value: 'HR',
    label: 'Croatia',
  },
  {
    value: 'HU',
    label: 'Hungary',
  },
  {
    value: 'IE',
    label: 'Ireland',
  },
  {
    value: 'IT',
    label: 'Italy',
  },
  {
    value: 'LI',
    label: 'Liechtenstein',
  },
  {
    value: 'LT',
    label: 'Lithuania',
  },
  {
    value: 'LU',
    label: 'Luxembourg',
  },
  {
    value: 'MA',
    label: 'Morocco',
  },
  {
    value: 'ME',
    label: 'Montenegro',
  },
  {
    value: 'MT',
    label: 'Malta',
  },
  {
    value: 'NL',
    label: 'Netherlands',
  },
  {
    value: 'NO',
    label: 'Norway',
  },
  {
    value: 'PL',
    label: 'Poland',
  },
  {
    value: 'PT',
    label: 'Portugal',
  },
  {
    value: 'RO',
    label: 'Romania',
  },
  {
    value: 'RS',
    label: 'Serbia',
  },
  {
    value: 'SE',
    label: 'Sweden',
  },
  {
    value: 'SI',
    label: 'Slovenia',
  },
  {
    value: 'SK',
    label: 'Slovakia',
  },
  {
    value: 'TN',
    label: 'Tunisia',
  },
  {
    value: 'TR',
    label: 'Turkey',
  },
].map((country) => ({ search: country.label, display: country.label, ...country }));

export const PickupSection = ({ ticketFormBodyData }) => {
  const { showErrors, isReadOnly, isDraft, backendErrors, values, draftErrors, initialValues } =
    ticketFormBodyData;
  const initialPostalCodeQuery = values?.order_data?.pickup?.zipcode || '';

  const zipcodeUnchanged =
    values.order_data.pickup.zipcode === initialValues.order_data.pickup.zipcode;

  const {
    zipcodeOptions,
    isLoadingZipcodesOptions,
    zipcodeOptionsError,
    setSearch,
    setCountry,
    setZipcode,
  } = usePostalCodeFilter({
    stop: 'pickup',
    initialQuery: initialPostalCodeQuery,
    country: values.order_data.pickup.country,
  });

  const hasInvalidZipcode = !!draftErrors?.order_data?.pickup?.zipcode;

  const showZipCodeBackendError = JSON.stringify(backendErrors).includes('pickup.zipcode');
  return (
    <SectionContainer>
      <SectionTitle>Pickup</SectionTitle>
      <SectionBody>
        <SectionLine>
          <FormikPremiumInput
            label={'Company name'}
            name="order_data.pickup.company_name"
            showErrors={showErrors}
            isDisabled={isReadOnly}
          />
        </SectionLine>
        <SectionLine>
          <FormikPremiumInput
            label="Address"
            showErrors={showErrors}
            name="order_data.pickup.address"
            isRequired={true}
            isDisabled={isReadOnly}
          />
        </SectionLine>
        <SectionLine>
          <FormikPremiumInput
            isRequired={true}
            label="City"
            showErrors={showErrors}
            name="order_data.pickup.city"
            isDisabled={isReadOnly}
          />
          <FormikPremiumInput
            type="select-with-search"
            onFieldChange={setCountry}
            label="Country"
            searchLabel={'Search country'}
            showErrors={showErrors}
            optionsEmptyMssg={'No country found'}
            isClereable={true}
            isDisabled={isReadOnly}
            isRequired={true}
            name="order_data.pickup.country"
            options={countryOptions}
          />
          <FormikPremiumInput
            type="select-with-search"
            onSearchChange={setSearch}
            onFieldChange={setZipcode}
            options={zipcodeOptions}
            isLoadingOptions={isLoadingZipcodesOptions}
            label={t.zipcode_label}
            searchLabel={t.search_label}
            showErrors={showErrors}
            customHasError={
              showZipCodeBackendError ||
              zipcodeOptionsError ||
              (zipcodeUnchanged && hasInvalidZipcode)
            }
            optionsEmptyMssg={t.no_zipcode}
            isClereable={true}
            isDisabled={isReadOnly && !isDraft}
            isRequired={true}
            initialValue={initialPostalCodeQuery}
            name="order_data.pickup.zipcode"
          />
        </SectionLine>
        {showZipCodeBackendError && (
          <SectionLine>
            <FieldErrorMessageContainer>
              <FieldErrorMessageText>
                Postal code not available for the selected country
              </FieldErrorMessageText>
            </FieldErrorMessageContainer>
          </SectionLine>
        )}
        <SectionLine>
          <FormikPremiumInput
            label="Date"
            showErrors={showErrors}
            name="order_data.pickup.date"
            type="date"
            isRequired={true}
            isDisabled={isReadOnly}
          />
          <FormikPremiumInput
            label="Start time"
            isRequired={true}
            showErrors={showErrors}
            name="order_data.pickup.time_from"
            type="time"
            isDisabled={isReadOnly}
          />
          <FormikPremiumInput
            label="End time"
            showErrors={showErrors}
            name="order_data.pickup.time_to"
            type="time"
            isRequired={true}
            isDisabled={isReadOnly}
          />
        </SectionLine>
        <SectionLine>
          <FormikPremiumInput
            isDisabled={isReadOnly}
            label="Observations"
            name="order_data.pickup.observations"
          />
        </SectionLine>
      </SectionBody>
    </SectionContainer>
  );
};
